import {useEffect, useState} from "react";
import {useSelector} from "react-redux";
import {useNavigate} from "react-router-dom";
import moment from "moment";

import Page from "@/components/Page";
import ChoiceChips from "@/components/ChoiceChips";
import Loading from "@/components/Loading";
import Breadcrumbs from "@/components/Breadcrumbs";
import MainActions from "@/components/MainActions";
import CardsContainer from "@/components/CardsContainer";
import CardsList from "@/components/CardsList";
import CardsTable from "@/components/CardsTable";
import StatsContainer from "@/components/StatsContainer";
import VLFModal from "@/components/Modal";
import VLFSelectNew from "@/components/Input/SelectNew";
import VLFDatePicker from "@/components/Input/DatePicker";
import VLFCard from "@/components/Card";
import {PlayerAPI} from "@/api/player";

import AddIcon from "@mui/icons-material/Add";
import SaveIcon from "@mui/icons-material/Save";

import {
	dateFormat,
	tqrOptions,
	ERROR_OPERATION,
	SUCCESSFUL_OPERATION,
	REQUIRED_FIELD,
} from "@/utils/constants";
import {checkRequiredFields} from "@/utils/functions";
import {useCategories} from "@/utils/hooks";

import "./index.scss";

const VIEW_OPTIONS = [
	{value: "list", label: "Elenco"},
	{value: "stats", label: "Per settimana"},
];

const FIELD_DATE_FORMAT = "YYYY-MM-DD";

const requiredFields = ["tqrValue"];

const STAFF_PERIODS = [
	{label: "7 giorni", days: 7},
	{label: "14 giorni", days: 14},
	{label: "30 giorni", days: 30},
];

const PLAYER_PERIODS = [
	{label: "7 giorni", days: 7},
	{label: "30 giorni", days: 30},
	{label: "3 mesi", days: 90},
];

function TQRPage(props) {
	const {handleNotification} = props;
	const navigate = useNavigate();

	const playerId = useSelector((state) => state?.user?.playerId);
	const userRole = useSelector((state) => state?.user?.roleName);
	const isPlayer = userRole === "Player";

	const periods = isPlayer ? PLAYER_PERIODS : STAFF_PERIODS;
	const defaultDays = isPlayer ? 30 : 7;

	const [activePeriod, setActivePeriod] = useState(defaultDays);
	const [currentSwitchView, setCurrentSwitchView] = useState("list");
	const [loading, setLoading] = useState(false);
	const [tqr, setTqr] = useState([]);

	// Inline add form (player only)
	const [addFields, setAddFields] = useState({});
	const [addLoading, setAddLoading] = useState(false);

	// Edit / delete
	const [openHandleConfirm, setOpenHandleConfirm] = useState(false);
	const [openHandleEdit, setOpenHandleEdit] = useState(false);
	const [editValue, setEditValue] = useState();
	const [currentTQR, setCurrentTQR] = useState({});
	const [editErrors, setEditErrors] = useState([]);

	const {categories} = useCategories({
		setLoading: false,
		handleNotification: undefined,
		onlyPro: true,
	});

	const filtersData = {
		fromDate: moment()
			.subtract(activePeriod - 1, "days")
			.format(FIELD_DATE_FORMAT),
		toDate: moment().format(FIELD_DATE_FORMAT),
	};

	const getTQR = async () => {
		setLoading(true);
		try {
			const response = isPlayer
				? await PlayerAPI.playerListTQR(playerId, filtersData)
				: await PlayerAPI.listTQR(filtersData);
			setTqr(response || []);
		} catch {
			handleNotification(ERROR_OPERATION, "error");
		} finally {
			setLoading(false);
		}
	};

	const saveTQR = async () => {
		setAddLoading(true);
		try {
			await PlayerAPI.createTQR({
				playerId,
				tqrDate: moment(addFields.date).format(FIELD_DATE_FORMAT),
				tqrValue: addFields.value?.value,
			});
			setAddFields({});
			getTQR();
			handleNotification(SUCCESSFUL_OPERATION, "success");
		} catch {
			handleNotification(ERROR_OPERATION, "error");
		} finally {
			setAddLoading(false);
		}
	};

	const deleteTQR = async () => {
		setLoading(true);
		try {
			await PlayerAPI.deleteTQR(
				isPlayer ? playerId : currentTQR?.playerId,
				currentTQR?.id,
			);
			getTQR();
			handleNotification(SUCCESSFUL_OPERATION, "success");
		} catch {
			handleNotification(ERROR_OPERATION, "error");
			setLoading(false);
		} finally {
			setOpenHandleConfirm(false);
		}
	};

	const updateTQR = async () => {
		setLoading(true);
		try {
			await PlayerAPI.updateTQR(
				isPlayer ? playerId : currentTQR?.playerId,
				currentTQR?.id,
				{...currentTQR, tqrValue: editValue?.value},
			);
			getTQR();
			handleNotification(SUCCESSFUL_OPERATION, "success");
		} catch {
			handleNotification(ERROR_OPERATION, "error");
			setLoading(false);
		} finally {
			setOpenHandleEdit(false);
		}
	};

	useEffect(() => {
		if (currentSwitchView === "list") {
			getTQR();
		}
	}, [activePeriod, currentSwitchView]);

	const filters = [
		{
			key: "fromDate",
			type: "date",
			label: "Da",
			customClassName: "col-6 col-sm-4",
			clearable: false,
			value: filtersData.fromDate,
			setValue: () => {},
		},
		{
			key: "toDate",
			type: "date",
			label: "A",
			customClassName: "col-6 col-sm-4",
			clearable: false,
			value: filtersData.toDate,
			setValue: () => {},
		},
	];

	const columns = [
		{
			label: "Data",
			key: "date",
			customRender: (item) => moment(item.tqrDate).format(dateFormat),
			small: true,
		},
	];

	if (!isPlayer) {
		columns.push({
			label: "Giocatore",
			key: "playerId",
			customRender: (item) =>
				item.player
					? item.player.firstName + " " + item.player.lastName
					: "---",
			align: "start",
		});
	}

	columns.push({
		label: "TQR",
		key: "tqr",
		customRender: (item) =>
			tqrOptions.find((o) => o.value === item.tqrValue)?.label ?? "---",
		align: "start",
	});

	const handleEdit = (item) => {
		setEditValue({
			value: item.tqrValue,
			label: tqrOptions.find((o) => o.value === item.tqrValue)?.label,
		});
		setCurrentTQR(item);
		setOpenHandleEdit(true);
	};

	const listConfiguration = {
		data: tqr,
		handleEdit,
		handleDelete: (item) => {
			setCurrentTQR(item);
			setOpenHandleConfirm(true);
		},
	};

	const staffAddAction = {
		icon: <AddIcon />,
		onClick: () => navigate("/measurations/tqr/new"),
	};

	return (
		<Page>
			<Loading visible={loading || addLoading} />

			<Breadcrumbs
				current="Recuperi"
				listElements={
					isPlayer
						? undefined
						: VIEW_OPTIONS.map((o) => ({
								...o,
								onClick: () => setCurrentSwitchView(o.value),
							}))
				}
				currentListElement={currentSwitchView}
				actions={!isPlayer ? <MainActions actions={[staffAddAction]} /> : null}
			/>

			{/* ── Player: quick-add inline form ── */}
			{isPlayer && (
				<div className="meas-quick-add">
					<VLFCard
						title="Come ti senti oggi? (recupero)"
						openable={false}
						content={
							<div className="meas-quick-add__fields">
								<div className="meas-quick-add__field">
									<VLFDatePicker
										label="Data"
										value={addFields.date}
										onChange={(v) => setAddFields({...addFields, date: v})}
										required
										size="small"
									/>
								</div>
								<div className="meas-quick-add__field">
									<VLFSelectNew
										options={tqrOptions}
										value={addFields.value || ""}
										onChange={(v) => setAddFields({...addFields, value: v})}
										label="Qualità recupero"
										sorted={false}
										required
									/>
								</div>
								<MainActions
									actions={[
										{
											icon: <SaveIcon />,
											label: "Salva",
											onClick: saveTQR,
											disabled: !addFields.date || !addFields.value,
										},
									]}
								/>
							</div>
						}
					/>
				</div>
			)}

			{currentSwitchView === "list" && (
				<ChoiceChips
					options={periods.map((p) => ({value: p.days, label: p.label}))}
					value={activePeriod}
					onChange={setActivePeriod}
				/>
			)}

			{currentSwitchView === "list" ? (
				<CardsContainer
					tableComponent={
						<CardsTable
							filtersData={filters}
							configurationColumns={columns}
							data={tqr}
							rowActions
							handleDelete={(item) => {
								setCurrentTQR(item);
								setOpenHandleConfirm(true);
							}}
							handleEdit={handleEdit}
							defaultSortedField="tqrDate"
							defaultSortedOrder="descend"
						/>
					}
					listComponent={
						<CardsList
							type="tqr"
							breadcrumbs
							filtersData={filters}
							listConfiguration={listConfiguration}
							defaultSortedField="tqrDate"
							defaultSortedOrder="descend"
						/>
					}
				/>
			) : (
				<StatsContainer type="tqr" categories={categories} />
			)}

			{openHandleConfirm && (
				<VLFModal
					open={openHandleConfirm}
					title="Eliminazione"
					content={
						<div className="mt-2">
							Sei sicuro di voler eliminare questo elemento?
						</div>
					}
					confirm={deleteTQR}
					confirmLabel="Conferma"
					close={() => setOpenHandleConfirm(false)}
				/>
			)}

			{openHandleEdit && (
				<VLFModal
					open={openHandleEdit}
					title="Modifica valore"
					content={
						<div className="row">
							<div className="col-12">
								<VLFSelectNew
									options={tqrOptions}
									value={editValue || ""}
									onChange={(v) => {
										setEditErrors([]);
										setEditValue(v);
									}}
									required
									label="TQR"
									sorted={false}
									helperText={
										editErrors.includes("tqrValue") ? REQUIRED_FIELD : ""
									}
									error={editErrors.includes("tqrValue")}
								/>
							</div>
						</div>
					}
					confirm={() => {
						const errors = checkRequiredFields(requiredFields, {
							tqrValue: editValue,
						});
						setEditErrors(errors);
						if (!errors.length) updateTQR();
					}}
					confirmLabel="Conferma"
					close={() => setOpenHandleEdit(false)}
				/>
			)}
		</Page>
	);
}

export default TQRPage;
